import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { generateIncidentReportPDF } from '../../utils/pdf';
import { IconArrowLeft, IconMapPin, IconFileTypePdf, IconCheck, IconX } from '@tabler/icons-react';

export default function IncidentDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, org } = useAuth();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [closing, setClosing] = useState(false);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(
          doc(db, 'organisations', resolvedOrgId, 'incidents', incidentId)
        );
        if (snap.exists()) {
          setIncident({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [incidentId, resolvedOrgId]);

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleExportPDF = async () => {
    if (!incident) return;
    setExportingPdf(true);
    try {
      generateIncidentReportPDF(incident, org);
    } catch (err) {
      console.error(err);
    }
    setExportingPdf(false);
  };

  const handleCloseIncident = async () => {
    if (!incident) return;
    setClosing(true);
    try {
      await updateDoc(doc(db, 'organisations', resolvedOrgId, 'incidents', incidentId), {
        status: 'closed',
        closedAt: serverTimestamp(),
        closedBy: userProfile?.name,
      });
      setIncident(prev => ({ ...prev, status: 'closed' }));
    } catch (err) {
      console.error(err);
    }
    setClosing(false);
  };

  const severityColour = s => s === 'high' ? 'var(--color-danger)' : s === 'medium' ? 'var(--color-warning)' : '#FAC775';
  const severityBg = s => s === 'high' ? 'var(--color-danger-light)' : s === 'medium' ? 'var(--color-warning-light)' : '#FDF0E8';
  const statusStyle = s => s === 'open'
    ? { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' }
    : s === 'review'
      ? { bg: 'var(--color-warning-light)', color: 'var(--color-warning)' }
      : { bg: 'var(--color-success-light)', color: 'var(--color-success)' };

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  if (!incident) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/incidents')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Incidents
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Incident not found</div>
      </div>
    </Layout>
  );

  const ss = statusStyle(incident.status || 'open');

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/incidents')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Incidents
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>
              Impact detected — {incident.assetName || 'Vehicle'}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-tertiary)', flexWrap: 'wrap' }}>
              {incident.assetReg && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 6px', borderRadius: 3 }}>{incident.assetReg}</span>
              )}
              {incident.driverName && <span>{incident.driverName}</span>}
              <span>· {formatTime(incident.detectedAt)}</span>
              {incident.severity && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: severityBg(incident.severity), color: severityColour(incident.severity) }}>
                  {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)} severity
                </span>
              )}
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: ss.bg, color: ss.color }}>
                {(incident.status || 'open').charAt(0).toUpperCase() + (incident.status || 'open').slice(1)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {incident.status !== 'closed' && (
              <button
                onClick={handleCloseIncident}
                disabled={closing}
                style={{ background: 'transparent', color: 'var(--color-success)', border: '0.5px solid var(--color-success)', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: closing ? 'not-allowed' : 'pointer', opacity: closing ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <IconCheck size={14} stroke={1.5} /> {closing ? 'Closing...' : 'Close incident'}
              </button>
            )}
            <button
              onClick={handleExportPDF}
              disabled={exportingPdf}
              style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: exportingPdf ? 'not-allowed' : 'pointer', opacity: exportingPdf ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <IconFileTypePdf size={14} stroke={1.5} /> {exportingPdf ? 'Generating...' : 'Export PDF'}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {[
                { label: 'G-force', value: incident.gforce ? `${incident.gforce}G` : '—', colour: 'var(--color-danger)' },
                { label: 'Speed', value: incident.speed ? `${incident.speed}mph` : '—', colour: 'var(--color-warning)' },
                { label: 'Time', value: incident.detectedAt ? new Date(incident.detectedAt.toDate ? incident.detectedAt.toDate() : incident.detectedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—', colour: 'var(--color-text-primary)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Location */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Location</div>
              <div style={{ height: 120, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                <IconMapPin size={24} color="var(--color-danger)" stroke={1.5} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{incident.location || 'Location not recorded'}</div>
            </div>

            {/* Timeline */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Timeline</div>
              {(incident.timeline || [
                { label: 'Impact detected automatically', time: formatTime(incident.detectedAt), colour: 'var(--color-danger)' },
                { label: 'Push notification sent to branch manager', time: formatTime(incident.detectedAt), colour: 'var(--color-warning)' },
                incident.status === 'closed' ? { label: `Incident closed by ${incident.closedBy || '—'}`, time: formatTime(incident.closedAt), colour: 'var(--color-success)' } : null,
              ].filter(Boolean)).map((t, i, arr) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: t.colour, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 13, color: 'var(--color-text-primary)' }}>{t.label}</div>
                  <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-tertiary)' }}>{t.time}</div>
                </div>
              ))}
            </div>

          </div>

          {/* Right column */}
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>

            {/* Driver details */}
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Driver details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginBottom: 20 }}>
              {[
                { label: 'Name', value: incident.driverName },
                { label: 'Licence', value: incident.driverLicence },
                { label: 'Categories', value: incident.licenceCategories?.join(', ') },
                { label: 'Endorsements', value: incident.endorsements != null ? String(incident.endorsements) : '—' },
              ].map(r => (
                <div key={r.label} style={{ padding: '7px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 2 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{r.value || '—'}</div>
                </div>
              ))}
            </div>

            {/* Driver notes */}
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>Driver notes</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 20, padding: '12px 14px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', minHeight: 48 }}>
              {incident.driverNotes || 'No notes submitted.'}
            </div>

            {/* Photos */}
            {incident.photos?.length > 0 && (
              <>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>Photos</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 20 }}>
                  {incident.photos.map((url, i) => (
                    <img key={i} src={url} alt={`Incident photo ${i + 1}`} style={{ height: 80, width: '100%', objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)' }} />
                  ))}
                </div>
              </>
            )}

            {/* Audit trail */}
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Audit trail</div>
            {[
              { label: 'Impact detected automatically', time: formatTime(incident.detectedAt), colour: 'var(--color-danger)' },
              { label: `Asset ${incident.assetReg || ''} flagged for inspection`, time: formatTime(incident.detectedAt), colour: 'var(--color-danger)' },
              { label: 'Push notification sent to branch manager', time: formatTime(incident.detectedAt), colour: 'var(--color-warning)' },
              incident.status === 'closed' ? { label: `Incident closed by ${incident.closedBy || '—'}`, time: formatTime(incident.closedAt), colour: 'var(--color-success)' } : null,
            ].filter(Boolean).map((a, i, arr) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: a.colour, marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>{a.label}</div>
                <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{a.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}